/**
 * Document Types — Stage 1 (read-only shadow).
 *
 * Captures the MIME baseline layer of the layered document-type
 * model described in FEATURE_REQUESTS.md §"Document Types & First-
 * Class Panels". For Stage 1 this module is purely derivational:
 * every document's mime maps to a baseline type, and that type is
 * surfaced on API responses for downstream tooling to consume. No
 * UI behavior changes yet.
 *
 * Later stages will:
 *   • Stage 2 — PreviewPane reads .previewRenderer instead of mime
 *     switching.
 *   • Stage 3 — Bundles/nests can register specializations that
 *     ADD to the baseline (never weaken it).
 *   • Stage 4 — Custom Properties migrate from applies_to_tags →
 *     applies_to_types.
 *   • Stage 5 — Server-side enforcement of the security floor.
 *
 * The vocabulary defined here is the closed set for the first pass.
 * If a new mime needs handling, add a baseline entry — don't extend
 * the renderer enum unless the renderer is fundamentally new.
 */
const PANELS_DEFAULT = {
    preview: true,
    customProperties: true,
    crawlTree: false,
    toolPalette: false,
    findings: false,
};
const PANELS_DOWNLOAD_ONLY = {
    preview: false,
    customProperties: true,
    crawlTree: false,
    toolPalette: false,
    findings: false,
};
/**
 * Baseline-type registry. Order matters only for documentation —
 * lookup is by exact mime first, then prefix, falling back to
 * `binary-doc` as the safe default.
 */
export const DOCUMENT_BASELINE_TYPES = [
    {
        id: 'html-doc',
        name: 'HTML document',
        mimeTypes: ['text/html', 'application/xhtml+xml'],
        mimePrefixes: [],
        previewRenderer: 'sandboxed-iframe',
        // Same posture as the 0.1.27 hotfix: sandboxed iframe + strict
        // CSP server-side. AI guard ON because hidden HTML (alt text,
        // off-screen divs, comments) is a known prompt-injection vector.
        security: { sandbox: true, csp: 'strict', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'svg-doc',
        name: 'SVG image',
        mimeTypes: ['image/svg+xml'],
        mimePrefixes: [],
        // SVG carries scripts (<script>, on* handlers). Must be sandboxed
        // when rendered as a document — the <img> path is safe because
        // browsers don't execute scripts in img, but exposing it through
        // /content with an HTML-like sniff would. CSP closes that.
        previewRenderer: 'sandboxed-iframe',
        security: { sandbox: true, csp: 'strict', aiPromptInjectionGuard: false, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'pdf-doc',
        name: 'PDF document',
        mimeTypes: ['application/pdf'],
        mimePrefixes: [],
        previewRenderer: 'pdf-iframe',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: false, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'docx-doc',
        name: 'Word document',
        mimeTypes: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        mimePrefixes: [],
        previewRenderer: 'docx-html',
        // Docx is converted to HTML at upload via mammoth and sanitized
        // (sanitizeDocxHtml strips scripts/iframes). Text content can
        // still carry prompt-injection language.
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'markdown-doc',
        name: 'Markdown document',
        mimeTypes: ['text/markdown'],
        mimePrefixes: [],
        previewRenderer: 'markdown',
        // dr.the0's specific concern: markdown is text + the chunker,
        // summarizer, and classifier feed it into LLMs. Hidden directives
        // like "Ignore all prior instructions" must be surfaced for the
        // operator before any LLM-bound action.
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'plain-text-doc',
        name: 'Plain text',
        mimeTypes: ['text/plain'],
        mimePrefixes: [],
        previewRenderer: 'plain-text',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'json-doc',
        name: 'JSON document',
        mimeTypes: ['application/json'],
        mimePrefixes: [],
        previewRenderer: 'source-with-highlight',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'yaml-doc',
        name: 'YAML document',
        mimeTypes: ['application/yaml', 'application/x-yaml', 'text/yaml', 'text/x-yaml'],
        mimePrefixes: [],
        previewRenderer: 'source-with-highlight',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'xml-doc',
        name: 'XML document',
        mimeTypes: ['application/xml', 'text/xml'],
        mimePrefixes: [],
        // XML can carry XXE / billion-laughs vectors when parsed by a
        // permissive parser. We render it as source only (no parsing,
        // no DOM building). AI guard ON — XML often wraps content that
        // gets fed into LLM pipelines (sitemaps, RSS, OPML, etc.).
        previewRenderer: 'source-with-highlight',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'csv-doc',
        name: 'CSV document',
        mimeTypes: ['text/csv', 'application/csv'],
        mimePrefixes: [],
        // First pass: render as syntax-highlighted source. A 'table'
        // renderer (proper CSV → grid) is a candidate for a later
        // baseline-type expansion if operators want filterable data.
        // AI guard ON because spreadsheet text often gets summarized
        // by LLMs and a poisoned header row can carry instructions.
        previewRenderer: 'source-with-highlight',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'ndjson-doc',
        name: 'NDJSON / JSON-Lines document',
        mimeTypes: ['application/x-ndjson', 'application/jsonl', 'application/ld+json', 'application/json-seq'],
        mimePrefixes: [],
        // Distinct from json-doc: NDJSON / JSON-Lines is one-object-per-
        // line, common in crawler output and event logs. Same renderer
        // for now; specialization may later add a "stream viewer" panel.
        previewRenderer: 'source-with-highlight',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'css-doc',
        name: 'CSS document',
        mimeTypes: ['text/css'],
        mimePrefixes: [],
        // CSS is generally lower-risk — it can't execute scripts — but
        // `url(javascript:…)` patterns and content-injection via
        // `@import` warrant care. Rendered as source only.
        previewRenderer: 'source-with-highlight',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: false, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'js-doc',
        name: 'JavaScript document',
        mimeTypes: ['text/javascript', 'application/javascript', 'application/x-javascript', 'application/ecmascript'],
        mimePrefixes: [],
        // JS is the highest-risk text format if anything mistakenly
        // routed it through an HTML renderer. Source-only is non-
        // negotiable. AI guard ON: copilot-style assistants are a
        // known prompt-injection target inside JS comments.
        previewRenderer: 'source-with-highlight',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: true, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'image-doc',
        name: 'Image',
        // Empty exact list — caught by prefix below (after svg-doc which
        // takes 'image/svg+xml').
        mimeTypes: [],
        mimePrefixes: ['image/'],
        previewRenderer: 'image',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: false, downloadOnly: false },
        defaultPanels: PANELS_DEFAULT,
    },
    {
        id: 'binary-doc',
        name: 'Binary file',
        mimeTypes: [],
        mimePrefixes: [],
        previewRenderer: 'download-only',
        security: { sandbox: false, csp: 'none', aiPromptInjectionGuard: false, downloadOnly: true },
        defaultPanels: PANELS_DOWNLOAD_ONLY,
    },
];
/**
 * Map a MIME string to its baseline type. Empty / unknown mimes fall
 * back to the `binary-doc` baseline (download-only).
 *
 * Precedence: exact mime match → prefix match → binary fallback.
 */
export function getBaselineType(mimeType) {
    const lower = (mimeType ?? '').toLowerCase().trim();
    if (lower) {
        for (const t of DOCUMENT_BASELINE_TYPES) {
            if (t.mimeTypes.includes(lower))
                return t;
        }
        for (const t of DOCUMENT_BASELINE_TYPES) {
            if (t.mimePrefixes.some((p) => lower.startsWith(p)))
                return t;
        }
    }
    // Fallback — the `binary-doc` entry is asserted to always exist.
    return DOCUMENT_BASELINE_TYPES[DOCUMENT_BASELINE_TYPES.length - 1];
}
export function summarizeBaselineType(t) {
    return { id: t.id, name: t.name, previewRenderer: t.previewRenderer };
}
//# sourceMappingURL=document-types.js.map